import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { LogoMark } from "../../components/Logo";
import { useAuth } from "../../lib/auth";
import { API_BASE, api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Shield, Users, AlertTriangle, Sparkles } from "lucide-react";

const roleIcon = { owner: ShieldCheck, admin: Shield, member: Users, viewer: Users };
const roleLabel = { owner: "Owner", admin: "Admin", member: "Member", viewer: "Viewer" };

export default function InviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setUserFromInvite } = useAuth();
  const [invite, setInvite] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/v1/team/invitations/lookup/${token}`);
        if (cancelled) return;
        setInvite(data);
        setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Invitation link is invalid or expired");
        setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/v1/team/accept", {
        token,
        name: form.name,
        password: form.password,
      });
      localStorage.setItem("sentry_token", data.token);
      // Nudge AuthProvider to hydrate
      if (typeof setUserFromInvite === "function") setUserFromInvite(data.user);
      toast.success(`Welcome to ${invite?.tenant_name}!`);
      // Small delay to let AuthContext settle then hard-navigate
      setTimeout(() => { window.location.href = "/app"; }, 300);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept invitation");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left panel */}
      <div className="starfield text-cream flex flex-col justify-between p-10 relative overflow-hidden">
        <Link to="/" className="inline-flex items-center gap-3 relative z-10">
          <LogoMark size={40} />
          <div className="font-display text-xl font-bold">Sentry</div>
        </Link>

        <div className="relative z-10 max-w-md">
          <div className="chip chip-cream mb-4"><Sparkles size={12} /> You've been invited</div>
          {status === "ready" && invite ? (
            <>
              <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">
                Join <span className="text-orange">{invite.tenant_name}</span> on Sentry.
              </h2>
              <p className="text-cream/70 mt-4">
                {invite.invited_by_name} invited you as <b className="text-cream">{roleLabel[invite.role] || invite.role}</b>.
                You'll get access to policies, decisions, and the causal-trace Observatory.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-cream/75">
                <li className="flex gap-2"><ShieldCheck size={16} className="text-[#FFD98A] mt-0.5" /> Every decision cryptographically signed</li>
                <li className="flex gap-2"><Shield size={16} className="text-[#FFD98A] mt-0.5" /> SentryDSL policies for prompt & PII guardrails</li>
                <li className="flex gap-2"><Users size={16} className="text-[#FFD98A] mt-0.5" /> Shared workspace with role-based access</li>
              </ul>
            </>
          ) : (
            <>
              <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">Welcome to Sentry</h2>
              <p className="text-cream/70 mt-4">The Governance OS for regulated AI.</p>
            </>
          )}
        </div>
        <div className="relative z-10 text-xs text-cream/50">© 2026 Lunar Occultation, Inc.</div>
      </div>

      {/* Right panel — action */}
      <div className="flex items-center justify-center p-10 bg-cream">
        <div className="w-full max-w-sm">
          {status === "loading" && (
            <div data-testid="invite-loading" className="flex flex-col items-center justify-center py-16 text-navy/60">
              <Loader2 size={22} className="animate-spin mb-3 text-[#FF6B35]" />
              <div className="text-sm">Verifying invitation…</div>
            </div>
          )}

          {status === "error" && (
            <div data-testid="invite-error" className="rounded-2xl bg-white border border-red-200 p-6">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                <AlertTriangle size={20} className="text-red-500" />
              </div>
              <h1 className="font-display text-2xl font-bold text-navy mt-4">Invitation unavailable</h1>
              <p className="ink text-sm mt-2">{error}</p>
              <div className="mt-6 flex gap-3">
                <Link to="/register" className="btn-primary text-sm">Start a new workspace</Link>
                <Link to="/login" className="btn-ghost text-sm">Sign in</Link>
              </div>
            </div>
          )}

          {status === "ready" && invite && (
            <form onSubmit={submit} data-testid="invite-accept-form">
              <h1 className="font-display text-3xl font-bold text-navy">Accept your invite</h1>
              <p className="ink mt-1 text-sm">
                {invite.email_hint
                  ? <>You're joining as <b className="text-navy">{invite.email_hint}</b>.</>
                  : <>You'll get a new account tied to this workspace.</>}
              </p>

              <div className="mt-5 rounded-xl bg-[#FAF7F2] border border-[rgba(10,14,26,0.08)] p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#FF6B35] flex items-center justify-center">
                  {(() => { const I = roleIcon[invite.role] || Users; return <I size={16} className="text-white" />; })()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] uppercase tracking-widest text-navy/50">Role</div>
                  <div className="text-sm font-semibold text-navy">{roleLabel[invite.role] || invite.role}</div>
                </div>
                <div className="text-[11px] text-navy/50 text-right">
                  <div>Invited by</div>
                  <div className="text-navy font-semibold">{invite.invited_by_name}</div>
                </div>
              </div>

              <div className="space-y-3 mt-5">
                <div>
                  <label className="eyebrow text-navy/70 text-xs">Full name</label>
                  <input
                    data-testid="invite-name"
                    required
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    className="w-full mt-1 px-3 py-2.5 rounded-lg border border-[rgba(10,14,26,0.15)] bg-white outline-none focus:ring-2 focus:ring-[#FF6B35]/40"
                  />
                </div>
                <div>
                  <label className="eyebrow text-navy/70 text-xs">Password</label>
                  <input
                    data-testid="invite-password"
                    type="password"
                    required
                    minLength={6}
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    className="w-full mt-1 px-3 py-2.5 rounded-lg border border-[rgba(10,14,26,0.15)] bg-white outline-none focus:ring-2 focus:ring-[#FF6B35]/40"
                  />
                </div>
              </div>

              <button
                data-testid="invite-submit"
                disabled={submitting}
                className="btn-primary w-full justify-center mt-6"
              >
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Joining…</> : <>Join {invite.tenant_name}</>}
              </button>

              <div className="text-[11px] ink mt-3 text-center">
                By joining you agree to Sentry's Terms and Privacy Notice.
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
